import {
  API_TYPES,
  type CaseDataForVisit,
  type GetCasesRequestQuery,
  type GetCasesResponse,
  getVisitOccurrencesResponse,
  type GetVisitWithCaseDataResponse,
  getVisitWithCaseDataResponse,
  type HcpMissingRequirementsForDate,
  type VisitOccurrenceData as BffVisitOccurrenceData,
  type VisitOccurrenceStatus as BffVisitOccurrenceStatus,
} from "@clipboard-health/contract-worker-app-bff";
import { z } from "zod";

import { BAA_SIGNATURE_NAME, VisitPricingTypes } from "./constants";

export type VisitWithCaseData = GetVisitWithCaseDataResponse["data"];
export type CaseData = CaseDataForVisit;
export type PatientData = CaseData["attributes"]["patient"];
export type VisitOccurrenceData = BffVisitOccurrenceData;
export type VisitOccurrenceStatus = BffVisitOccurrenceStatus;

export type PricingType = VisitWithCaseData["attributes"]["pricingType"];
export type VisitType = VisitWithCaseData["attributes"]["type"];
export type VisitStatus = VisitWithCaseData["attributes"]["status"];

export type CaseStatus = CaseData["attributes"]["status"];

export type CaseHistoryData = GetCasesResponse["data"][0];

export type MissingRequirement = HcpMissingRequirementsForDate;

export type GetGlobalStatisticsRequestQuery = Omit<GetCasesRequestQuery, "filter"> & {
  filter: Omit<GetCasesRequestQuery["filter"], "qualifications"> & {
    qualifications: string;
  };
};

const signatureSchema = z.object({
  attributes: z.object({
    documentName: z.literal(BAA_SIGNATURE_NAME),
    version: z.string(),
  }),
  type: z.literal("signature"),
});

export type Signature = z.infer<typeof signatureSchema>;

export const addSignatureRequestSchema = z.object({
  data: signatureSchema.extend({
    attributes: signatureSchema.shape.attributes.extend({
      signedDocumentHTML: z.string(),
    }),
  }),
});
export type AddSignatureRequest = z.infer<typeof addSignatureRequestSchema>;

export const addSignatureResponseSchema = z.object({
  data: signatureSchema,
});
export type AddSignatureResponse = z.infer<typeof addSignatureResponseSchema>;

export const signaturesResponseSchema = z.object({
  data: z.array(signatureSchema),
});
export type SignaturesResponse = z.infer<typeof signaturesResponseSchema>;

export const bookVisitRequestSchema = z.object({
  data: z.object({
    attributes: z.object({
      bookedWorkerId: z.string().nullable(),
    }),
    type: z.literal(API_TYPES.inHomeVisits),
  }),
});
export type BookVisitRequest = z.infer<typeof bookVisitRequestSchema>;

export const visitResponseSchema = getVisitWithCaseDataResponse.omit({ included: true });

export type BookVisitResponse = z.infer<typeof visitResponseSchema>;

export const logVisitRequestSchema = z.object({
  data: z.object({
    attributes: z.object({
      completedAt: z.string(),
      pricingType: z.union([
        z.literal(VisitPricingTypes.PER_HOUR),
        z.literal(VisitPricingTypes.PER_VISIT),
      ]),
      estimatedDuration: z.number().optional(),
    }),
    type: z.literal("visitOccurrence"),
  }),
});
export type LogVisitRequest = z.infer<typeof logVisitRequestSchema>;

const legacyVisitOccurrenceSchema = getVisitOccurrencesResponse.shape.data.element
  .omit({ type: true })
  .extend({ type: z.literal("visitOccurrence") });

export const logVisitResponseSchema = z.object({
  data: legacyVisitOccurrenceSchema,
});

export type LogVisitResponse = z.infer<typeof logVisitResponseSchema>;

/**
 * The Home Health API returns a different visit occurrence type than the BFF API.
 * Because of HH deprioritzation, we need to use this type for the legacy visit occurrence data
 * for the time being.
 */
export type LegacyVisitOccurrenceData = z.infer<typeof legacyVisitOccurrenceSchema>;

export const confirmVisitRequestSchema = z.object({
  data: z.object({
    attributes: z.object({
      confirmedVisitDate: z.string(),
    }),
    type: z.literal(API_TYPES.inHomeVisits),
  }),
});
export type ConfirmVisitRequest = z.infer<typeof confirmVisitRequestSchema>;

export const pendVisitRequestSchema = z.object({
  data: z.object({
    attributes: z.object({
      pendingVisitReason: z.string(),
    }),
    type: z.literal(API_TYPES.inHomeVisits),
  }),
});
export type PendVisitRequest = z.infer<typeof pendVisitRequestSchema>;
